import { createEffect, createSignal, For } from 'solid-js'
import AccountSidebar from './AccountSidebar'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import Footer from '/web/shared/aifans/Footer'
import { chatStore } from '/web/store'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { SubscriptionEvent, TokenTransaction } from '/common/types/aifans-schema'
import { useNavigate, useParams } from '@solidjs/router'
import { A } from '@solidjs/router'
import { formatTransactionType } from '/web/shared/aifans/lib/transaction-util'
import { getModelUrl } from '/web/shared/aifans/lib/model-util'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import { Link, Meta, Title } from '@solidjs/meta'

type TableData = SubscriptionEvent | TokenTransaction

export default function PaymentHistory() {
  const params = useParams()
  const navigate = useNavigate()
  const [tableData, setTableData] = createSignal<TableData[]>([])
  const [totalPages, setTotalPages] = createSignal(1)
  const pageSize = 10

  createEffect(async () => {
    const currentPage = Number(params.page) || 1
    await loadData(currentPage)
  })

  const loadData = async (page: number) => {
    const res = await aifansSubscriptionApi.getTransactions(page, pageSize)
    if (res.result) {
      setTableData(res.result.transactions)
      setTotalPages(Math.ceil(res.result.totalCount / pageSize))
    }
  }

  const changePage = (page: number) => {
    navigate(page === 1 ? '/payment-history' : `/payment-history/${page}`)
  }

  const characterFromId = (characterId: string) => {
    const char = chatStore().allChars.list.find((c) => c._id === characterId)
    return char
  }

  const formatDate = (dateString: string) => {
    return {
      date: new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(dateString)),
      time: new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }).format(new Date(dateString)),
    }
  }

  return (
    <>
      <Title>CosplayFans - Payment History</Title>
      <Meta name="og:title" content="CosplayFans - Payment History" />
      <Meta name="description" content="View your payment history on CosplayFans." />
      <Meta name="og:description" content="View your payment history on CosplayFans." />
      <Link rel="canonical" href={getCanonicalUrl('/payment-history/')} />
      <HeaderContainer />
      <main class="payment-history page-bg relative overflow-hidden bg-main pb-20 pt-20 sm:pt-28 md:bg-none md:pb-36">
        <div class="container relative z-20">
          <div class="wrapper gap-8 lg:flex">
            <AccountSidebar />

            {/* right */}
            <div class="right flex w-full flex-col">
              {/* head */}
              <div class="head gray-to-tran mb-3 rounded-xl text-2xl font-semibold sm:mb-7 sm:block sm:p-4 sm:text-[28px]">
                <h2>Payment History</h2>
              </div>
              <div class="gray-to-tran h-full w-full rounded-xl sm:p-6">
                {/* payment table */}
                {tableData().length ? (
                  <div class="payment-table mt-6 inline-block w-full overflow-x-auto font-normal">
                    <table class="w-full table-auto">
                      <thead>
                        <tr>
                          <th class="hidden py-3 text-left sm:table-cell">Date</th>
                          <th class="py-3 text-left">Type</th>
                          <th class="hidden py-3 text-left sm:table-cell">Name</th>
                          <th class="py-3 !text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData().map((item, i) => {
                          let amount = item.amount ? `$${item.amount.toFixed(2)}` : '$0.00'
                          let tokenSubtext = ''
                          const isTokenPurchase = item.type === 'token_purchase'
                          // Check for token info in both TokenTransaction and SubscriptionEvent
                          if ('tokenAmount' in item && item.tokenAmount !== 0) {
                            const tokensToShow =
                              item.tokensAdded !== undefined ? item.tokensAdded : item.tokenAmount
                            if (tokensToShow !== undefined && tokensToShow > 0) {
                              tokenSubtext = `+${tokensToShow} tokens`
                            }
                          } else if (
                            'tokensAdded' in item &&
                            item.tokensAdded !== undefined &&
                            item.tokensAdded > 0
                          ) {
                            // Handle SubscriptionEvent with token info
                            tokenSubtext = `+${item.tokensAdded} tokens`
                          }
                          const char = characterFromId(item.characterId!)
                          const characterInfo =
                            !isTokenPurchase && char ? (
                              <A
                                href={getModelUrl(char.categorySlug, char.slug)}
                                class="text-[#10e0f9] hover:underline"
                              >
                                <div class="text-base">{char.name}</div>
                                {char.realName && (
                                  <div class="text-xs text-gray-400">{char.realName}</div>
                                )}
                              </A>
                            ) : (
                              ''
                            )

                          return (
                            <tr class={i !== 0 ? 'border-t border-gray-700' : ''}>
                              <td class="hidden py-4 align-top sm:table-cell">
                                <div class="text-base font-medium">
                                  {formatDate(item.date).date}
                                </div>
                                <div class="text-xs text-gray-400">
                                  {formatDate(item.date).time}
                                </div>
                              </td>
                              <td class="py-4 align-top">
                                <div class="font-medium">
                                  {isTokenPurchase ? 'Tokens' : formatTransactionType(item.type)}
                                  {item.type === 'initial_payment' && (
                                    <div class="text-xs text-gray-400">Initial Payment</div>
                                  )}
                                  {item.type === 'recurring_payment' && (
                                    <div class="text-xs text-gray-400">Recurring</div>
                                  )}
                                </div>
                                <div class="flex flex-col gap-1 text-xs text-gray-400 sm:hidden">
                                  <span>
                                    {formatDate(item.date).date} {formatDate(item.date).time}
                                  </span>
                                  {!isTokenPurchase && char && (
                                    <A
                                      href={getModelUrl(char.categorySlug, char.slug)}
                                      class="text-[#10e0f9] hover:underline"
                                    >
                                      {char.name}
                                      {char.realName && ` - ${char.realName}`}
                                    </A>
                                  )}
                                </div>
                              </td>
                              <td class="hidden py-4 align-top sm:table-cell">{characterInfo}</td>
                              <td class="py-4 pr-4 !text-right align-top">
                                <div class="font-medium text-[#10e0f9]">{amount}</div>
                                {tokenSubtext && (
                                  <div class="text-xs text-gray-400">{tokenSubtext}</div>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    <div class="pagination mt-4 flex justify-center gap-2">
                      <For each={Array.from({ length: totalPages() }, (_, i) => i + 1)}>
                        {(page) => (
                          <button
                            onClick={() => changePage(page)}
                            class={`rounded px-3 py-1 ${
                              Number(params.page || '1') === page
                                ? 'bg-cosplay-purple text-white'
                                : 'bg-gray-600'
                            }`}
                          >
                            {page}
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                ) : (
                  <div class="empty-state py-8 text-center">
                    <p class="text-xl text-gray-400">No payment history available.</p>
                    <p class="mt-2 text-sm text-gray-500">
                      Your transactions will appear here once you make a purchase.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* bg */}
        <div class="radial-bg bottom-[-40%] left-[-250px] right-0 h-[1000px] w-[900px] opacity-10 md:hidden"></div>
      </main>
      <Footer />
    </>
  )
}
